// Browser entry, bundled by esbuild into build/rainbucks.js as the global `RB`
// (npm run build; wrangler dev/deploy run it automatically via [build]).
//
//   RB.schemas / RB.validate   shared Zod rules (src/shared/schemas.js)
//   RB.queryClient             TanStack Query client: caching, dedupe, retries
//   RB.queries                 read requests, cached by key
//   RB.mutations               write requests; refresh the reads they affect
//   RB.ApiError                thrown for non-2xx responses ({ status, data })
import { QueryClient, QueryObserver } from '@tanstack/query-core';
import qrcode from 'qrcode-generator';
import * as schemas from '../shared/schemas.js';

export { schemas, QueryObserver };
export const validate = schemas.validate;

// The Worker returns the browser-safe token and host from its environment.
// All app call sites use this one facade through the existing RB bundle.
let posthogClient;
const posthogQueue = [];
const posthogLogQueue = [];

function callPostHog(method, args) {
  if (posthogClient) return posthogClient[method](...args);
  posthogQueue.push([method, args]);
}

function callPostHogLogger(level, args) {
  if (posthogClient) return posthogClient.logger[level](...args);
  posthogLogQueue.push([level, args]);
}

export const posthog = {
  capture: (...args) => callPostHog('capture', args),
  identify: (...args) => callPostHog('identify', args),
  reset: (...args) => callPostHog('reset', args),
};

// This dedicated logger is the only path that exports application logs. It
// deliberately does not wrap console or the app's existing logging outputs.
export const posthogLogger = {
  info: (...args) => callPostHogLogger('info', args),
  warn: (...args) => callPostHogLogger('warn', args),
  error: (...args) => callPostHogLogger('error', args),
};

// /api/me is refreshed while signed in, so identify only once for the current
// authenticated browser session. The browser-facing API already returns the
// stable UUID and email required here.
let identifiedUserId = null;

export function identifyUser({ id, email }) {
  if (typeof id !== 'string' || !id || identifiedUserId === id) return;
  if (identifiedUserId) posthog.reset();
  posthog.identify(id, { email });
  identifiedUserId = id;
}

export function resetUserIdentity() {
  posthog.reset();
  identifiedUserId = null;
}

function missingPostHogConfig(variable) {
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    throw new Error(`${variable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${variable} is configured`);
  }
}

async function initializePostHog() {
  const response = await fetch('/api/posthog-config');
  const config = await response.json();
  if (!config.projectToken) return missingPostHogConfig('POSTHOG_PROJECT_TOKEN');
  if (!config.host) return missingPostHogConfig('POSTHOG_HOST');

  const script = document.createElement('script');
  script.async = true;
  script.src = `${config.host.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;
  script.onload = () => {
    window.posthog.init(config.projectToken, {
      api_host: config.host,
      defaults: '2026-05-30',
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      logs: {
        serviceName: 'rainbucks-web',
        environment: location.hostname === 'localhost' || location.hostname === '127.0.0.1' ? 'development' : 'production',
      },
    });
    posthogClient = window.posthog;
    for (const [method, args] of posthogQueue.splice(0)) posthogClient[method](...args);
    for (const [level, args] of posthogLogQueue.splice(0)) posthogClient.logger[level](...args);
    posthogLogger.info('posthog_logs_ready', { runtime: 'browser' });
  };
  document.head.appendChild(script);
}

initializePostHog().catch(() => {
  // Production stays a no-op when runtime configuration cannot be read.
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    throw new Error('POSTHOG_PROJECT_TOKEN variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once POSTHOG_PROJECT_TOKEN is configured');
  }
});

// Renders `text` as a scannable QR code (SVG string), entirely client-side -
// used for "scan with your phone to add this pass" on desktop, so the signed
// pass URL (short-lived but still sensitive) never gets sent to a third-party
// QR-image service.
export function qrSvg(text) {
  const qr = qrcode(0, 'M'); // typeNumber 0 = auto-sized, error correction M
  qr.addData(text);
  qr.make();
  return qr.createSvgTag({ cellSize: 6, margin: 2 });
}

export class ApiError extends Error {
  constructor(status, data) {
    super((data && data.error) || `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.data = data || {};
  }
}

// JSON request helper: throws ApiError on a non-2xx response, and a plain
// Error when the network or the JSON body fails.
export async function request(path, { method = 'GET', body } = {}) {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    posthogLogger.warn('api_request_failed', { status_code: res.status });
    throw new ApiError(res.status, data);
  }
  return data;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retry network and server errors twice; never retry 4xx, which won't change.
      retry: (failureCount, error) => !(error instanceof ApiError && error.status < 500) && failureCount < 2,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});
queryClient.mount(); // enables refetch on window focus / reconnect

export const keys = {
  me: ['me'],
  history: ['history'],
  offers: (category) => ['offers', category || 'all'],
  offerPerformance: ['offerPerformance'],
  offerStarts: ['offerStarts'],
};

export const queries = {
  me: () => queryClient.fetchQuery({ queryKey: keys.me, queryFn: () => request('/api/me'), staleTime: 0 }),
  history: () => queryClient.fetchQuery({ queryKey: keys.history, queryFn: () => request('/api/history') }),
  // Earliest-click-per-offer timestamps, used only to seed the detail
  // screen's countdown timers - see handleOfferStarts in src/worker.js.
  offerStarts: () => queryClient.fetchQuery({ queryKey: keys.offerStarts, queryFn: () => request('/api/offer-starts') }),
  // Glitchy's SDK does the actual call; the cache dedupes simultaneous loads of
  // the same list and keeps it for 5 minutes. Client errors (4xx: rate limits,
  // a domain not allowed for the token) won't change on retry, so they aren't retried.
  offers: (category, loader) =>
    queryClient.fetchQuery({
      queryKey: keys.offers(category),
      queryFn: loader,
      staleTime: 5 * 60_000,
      retry: (n, error) => !/\b4\d\d\b|not in the allowed domains/i.test(String(error && error.message)) && n < 1,
    }),
  // Real per-offer click->revenue stats, used to rank/feature offers by
  // actual earnings instead of just the advertiser's stated payout. Changes
  // slowly (aggregate, server-wide), so a long staleTime is fine.
  offerPerformance: () =>
    queryClient.fetchQuery({ queryKey: keys.offerPerformance, queryFn: () => request('/api/offers/performance'), staleTime: 10 * 60_000 }),
};

// Balance-changing writes refresh the reads that show a balance.
function refreshAccount() {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: keys.me }),
    queryClient.invalidateQueries({ queryKey: keys.history }),
  ]);
}

// Each mutation validates with the shared schema first, so bad input never
// leaves the browser; the Worker validates again with the same schema.
function checked(schema, input) {
  const v = validate(schema, input);
  if (!v.ok) throw new ApiError(400, { error: v.formError, fieldErrors: v.fieldErrors });
  return v.data;
}

export const mutations = {
  signup: async (input) => {
    const data = await request('/api/signup', { method: 'POST', body: checked(schemas.signupSchema, input) });
    queryClient.clear();
    return data;
  },
  login: async (input) => {
    const data = await request('/api/login', { method: 'POST', body: checked(schemas.loginSchema, input) });
    queryClient.clear();
    return data;
  },
  googleAuth: async (input) => {
    const data = await request('/api/auth/google', { method: 'POST', body: checked(schemas.googleAuthSchema, input) });
    queryClient.clear();
    return data;
  },
  logout: async () => {
    const data = await request('/api/logout', { method: 'POST' });
    queryClient.clear();
    return data;
  },
  bonus: async (input) => {
    const data = await request('/api/bonus', { method: 'POST', body: checked(schemas.bonusSchema, input) });
    await refreshAccount();
    return data;
  },
  cashout: async (input) => {
    const data = await request('/api/cashout', { method: 'POST', body: checked(schemas.cashoutSchema, input) });
    await refreshAccount();
    posthogLogger.info('cashout_request_completed');
    return data;
  },
  // Fire-and-forget: a failed survey save shouldn't block the onboarding flow,
  // so callers don't need to await/catch this.
  surveyAnswer: (input) => request('/api/survey', { method: 'POST', body: checked(schemas.surveyAnswerSchema, input) }),
  // Fire-and-forget: analytics only (admin funnel metrics), never block the
  // click-through to the offer if this fails.
  trackClick: (input) => request('/api/track-click', { method: 'POST', body: checked(schemas.trackClickSchema, input) }),
  // Fire-and-forget, no body: server-side mirror of the client's localStorage
  // streak, used only to drive push-notification eligibility (see
  // src/notifications/cron.js) - never affects the balance shown here.
  streakBump: () => request('/api/streak/bump', { method: 'POST', body: {} }),
};
